import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  onSnapshot,
  query,
  where 
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { GridData, GridCell, UserGridProgress } from '../types/grid'
import singaporeGridData from '../data/singapore_grid_export_2025-06-21.json'

// Load the Singapore grid data
export const getGridData = (): GridData => {
  return singaporeGridData as GridData
}

// Get user's grid progress from Firestore
export const getUserGridProgress = async (userId: string): Promise<UserGridProgress | null> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return docSnap.data() as UserGridProgress
    } else {
      // Create new progress document for user
      const newProgress: UserGridProgress = {
        userId,
        exploredCells: {},
        lastUpdated: new Date().toISOString()
      }
      await setDoc(docRef, newProgress)
      return newProgress
    }
  } catch (error) {
    console.error('Error getting user grid progress:', error)
    return null
  }
}

// Mark a grid cell as explored
export const markCellAsExplored = async (
  userId: string, 
  cellId: number, 
  notes?: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    const exploredDate = new Date().toISOString()
    
    await updateDoc(docRef, {
      [`exploredCells.${cellId}`]: {
        status: 'explored',
        exploredDate,
        notes: notes || ''
      },
      lastUpdated: exploredDate
    })
    
    return true
  } catch (error) {
    console.error('Error marking cell as explored:', error)
    return false
  }
}

// Mark a grid cell as inaccessible
export const markCellAsInaccessible = async (
  userId: string, 
  cellId: number, 
  notes?: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    const exploredDate = new Date().toISOString()
    
    await updateDoc(docRef, {
      [`exploredCells.${cellId}`]: {
        status: 'inaccessible',
        exploredDate,
        notes: notes || ''
      },
      lastUpdated: exploredDate
    })
    
    return true
  } catch (error) {
    console.error('Error marking cell as inaccessible:', error)
    return false
  }
}

// Update notes for a grid cell
export const updateCellNotes = async (
  userId: string, 
  cellId: number, 
  notes: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    
    await updateDoc(docRef, {
      [`exploredCells.${cellId}.notes`]: notes,
      lastUpdated: new Date().toISOString()
    })
    
    return true
  } catch (error) {
    console.error('Error updating cell notes:', error)
    return false
  }
}

// Update explored date for a grid cell
export const updateCellExploredDate = async (
  userId: string, 
  cellId: number, 
  date: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    
    await updateDoc(docRef, {
      [`exploredCells.${cellId}.exploredDate`]: date,
      lastUpdated: new Date().toISOString()
    })
    
    return true
  } catch (error) {
    console.error('Error updating cell explored date:', error)
    return false
  }
}

// Mark a grid cell as unexplored
export const markCellAsUnexplored = async (
  userId: string, 
  cellId: number
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    
    await updateDoc(docRef, {
      [`exploredCells.${cellId}`]: null,
      lastUpdated: new Date().toISOString()
    })
    
    return true
  } catch (error) {
    console.error('Error marking cell as unexplored:', error)
    return false
  }
}

// Subscribe to user's grid progress changes
export const subscribeToUserProgress = (
  userId: string, 
  callback: (progress: UserGridProgress | null) => void
) => {
  const docRef = doc(db, 'userGridProgress', userId)
  
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data() as UserGridProgress)
    } else {
      callback(null)
    }
  })
}

// Get exploration statistics
export const getExplorationStats = (gridData: GridData, userProgress: UserGridProgress | null) => {
  const totalCells = gridData.cells.length
  const exploredCells = userProgress ? Object.keys(userProgress.exploredCells).filter(id => 
    userProgress.exploredCells[parseInt(id)]?.status === 'explored'
  ).length : 0
  const inaccessibleCells = userProgress ? Object.keys(userProgress.exploredCells).filter(id => 
    userProgress.exploredCells[parseInt(id)]?.status === 'inaccessible'
  ).length : 0
  const completedCells = exploredCells + inaccessibleCells
  const unexploredCells = totalCells - completedCells
  const progressPercentage = Math.round((completedCells / totalCells) * 100)
  
  // Count by region
  const regionStats = gridData.cells.reduce((acc, cell) => {
    const cellProgress = userProgress?.exploredCells[cell.id]
    const isExplored = cellProgress?.status === 'explored'
    const isInaccessible = cellProgress?.status === 'inaccessible'
    
    if (!acc[cell.regionName]) {
      acc[cell.regionName] = { total: 0, explored: 0, inaccessible: 0 }
    }
    acc[cell.regionName].total++
    if (isExplored) {
      acc[cell.regionName].explored++
    }
    if (isInaccessible) {
      acc[cell.regionName].inaccessible++
    }
    
    return acc
  }, {} as Record<string, { total: number; explored: number; inaccessible: number }>)
  
  return {
    totalCells,
    exploredCells,
    inaccessibleCells,
    completedCells,
    unexploredCells,
    progressPercentage,
    regionStats
  }
}

// Update custom grid name
export const updateCustomGridName = async (
  userId: string, 
  cellId: number, 
  customName: string
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    
    await updateDoc(docRef, {
      [`customNames.${cellId}`]: customName,
      lastUpdated: new Date().toISOString()
    })
    
    return true
  } catch (error) {
    console.error('Error updating custom grid name:', error)
    return false
  }
}

// Remove custom grid name (revert to default)
export const removeCustomGridName = async (
  userId: string, 
  cellId: number
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'userGridProgress', userId)
    
    await updateDoc(docRef, {
      [`customNames.${cellId}`]: null,
      lastUpdated: new Date().toISOString()
    })
    
    return true
  } catch (error) {
    console.error('Error removing custom grid name:', error)
    return false
  }
}

/** Ray-casting point-in-polygon. Bounds are [lat, lng] rings. */
export const isPointInCellBounds = (
  lat: number,
  lng: number,
  bounds: number[][]
): boolean => {
  if (!bounds || bounds.length < 3) return false

  let inside = false
  for (let i = 0, j = bounds.length - 1; i < bounds.length; j = i++) {
    const [latI, lngI] = bounds[i]
    const [latJ, lngJ] = bounds[j]
    const crosses =
      lngI > lng !== lngJ > lng &&
      lat < ((latJ - latI) * (lng - lngI)) / (lngJ - lngI) + latI
    if (crosses) inside = !inside
  }
  return inside
}

/** Find the grid cell that contains a GPS point, or null if outside the grid. */
export const findCellContainingPoint = (
  lat: number,
  lng: number,
  cells: GridCell[]
): GridCell | null => {
  for (const cell of cells) {
    if (isPointInCellBounds(lat, lng, cell.bounds)) {
      return cell
    }
  }
  return null
}

/** Max GPS accuracy (meters) allowed before auto-marking a cell. */
export const GPS_MARK_MAX_ACCURACY_METERS = 75

/** Axis-aligned map viewport in lat/lng. */
export type MapViewport = {
  south: number
  west: number
  north: number
  east: number
}

/** Precomputed cell bounding box for fast viewport tests. */
export type CellBBox = {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

export const getCellBBox = (bounds: number[][]): CellBBox => {
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity

  for (const point of bounds) {
    const lat = point[0]
    const lng = point[1]
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
  }

  return { minLat, maxLat, minLng, maxLng }
}

export const buildCellBBoxIndex = (cells: GridCell[]): Map<number, CellBBox> => {
  const index = new Map<number, CellBBox>()
  for (const cell of cells) {
    index.set(cell.id, getCellBBox(cell.bounds))
  }
  return index
}

export const bboxIntersectsViewport = (bbox: CellBBox, viewport: MapViewport): boolean => {
  return !(
    bbox.maxLat < viewport.south ||
    bbox.minLat > viewport.north ||
    bbox.maxLng < viewport.west ||
    bbox.minLng > viewport.east
  )
}

export const viewportsNearlyEqual = (
  a: MapViewport | null,
  b: MapViewport,
  epsilon = 1e-7
): boolean => {
  if (!a) return false
  return (
    Math.abs(a.south - b.south) < epsilon &&
    Math.abs(a.west - b.west) < epsilon &&
    Math.abs(a.north - b.north) < epsilon &&
    Math.abs(a.east - b.east) < epsilon
  )
}

/** Great-circle distance in meters between two [lat, lng] points. */
export const distanceMeters = (
  from: [number, number],
  to: [number, number]
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const [lat1, lng1] = from
  const [lat2, lng2] = to
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Friendly distance label, e.g. "120 m away" or "1.2 km away". */
export const formatDistanceAway = (meters: number): string => {
  if (!Number.isFinite(meters) || meters < 0) return ''
  if (meters < 1000) return `${Math.round(meters)} m away`
  const km = meters / 1000
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km away`
}

/**
 * Label for a cell relative to the user.
 * If the user is inside the cell bounds, always "You are here"
 * (not distance to the cell center).
 */
export const getDistanceLabelForCell = (
  userPosition: [number, number],
  cell: Pick<GridCell, 'bounds' | 'center'>
): string => {
  const [lat, lng] = userPosition
  if (isPointInCellBounds(lat, lng, cell.bounds)) {
    return 'You are here'
  }
  return formatDistanceAway(
    distanceMeters(userPosition, cell.center as [number, number])
  )
}

// Enhanced grid data with simple grid numbering
export const getEnhancedGridData = () => {
  const originalData = getGridData()
  
  if (!originalData || !originalData.cells) {
    return originalData
  }

  const enhancedCells = originalData.cells.map((cell: any) => {
    return {
      ...cell,
      regionName: "Singapore",
      displayName: `Grid ${cell.id}`
    }
  })

  return {
    ...originalData,
    cells: enhancedCells
  }
} 